import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArticleCard } from "@/components/article-card";
import { ArrowLeft } from "lucide-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { fetchTagPosts, fetchSettings } from "@/lib/api";

export const Route = createFileRoute("/tag/$slug")({
  loader: async ({ params, context: { queryClient } }) => {
    try {
      const [data, settings] = await Promise.all([
        queryClient.ensureQueryData({
          queryKey: ["tag-posts", params.slug],
          queryFn: () => fetchTagPosts(params.slug),
        }),
        queryClient.ensureQueryData({
          queryKey: ["settings"],
          queryFn: fetchSettings,
        }),
      ]);
      return { ...data, settings };
    } catch (e) {
      throw notFound();
    }
  },
  head: ({ loaderData, params }) => {
    const data = loaderData as any;
    const settings = data?.settings;
    const tag = data?.tag;
    const siteName = settings?.site_name || "Lihar Aksara";
    const title = `#${tag?.name || "Tag"} — ${siteName}`;
    const description = `Jelajahi semua tulisan dengan tagar #${tag?.name || "ini"} di ${siteName}.`;
    const canonicalUrl = `https://liharaksara.web.id/tag/${params.slug}`;

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
  component: TagPage,
});

function TagPage() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery({
    queryKey: ["tag-posts", slug],
    queryFn: () => fetchTagPosts(slug),
  }) as any;

  const tag = data?.tag;
  const posts = data?.posts || [];

  return (
    <div className="container-editorial py-16 md:py-24 bg-background">
      <Link
        to="/kategori"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-accent transition-colors mb-12"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Eksplorasi
      </Link>

      <div className="mb-16">
        <h1 className="font-serif text-5xl md:text-7xl font-medium tracking-tight flex items-center gap-4">
          <span className="text-muted-foreground/30">#</span>{tag?.name}
        </h1>
        {tag?.description && (
          <p className="mt-6 text-xl text-muted-foreground font-serif max-w-2xl italic">
            {tag.description}
          </p>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-x-8 md:gap-y-16">
        {posts.map((post: any) => (
          <ArticleCard key={post.id} article={post} />
        ))}
      </div>

      {posts.length === 0 && (
        <div className="py-32 text-center border border-dashed border-border rounded-sm opacity-50 font-serif italic text-lg">
          Belum ada tulisan dengan tagar ini.
        </div>
      )}
    </div>
  );
}
